/**
 * RunnabilityKpi — KPI strip at the top of the Workflows tab (Agents panel;
 * Loop Runtime PRD §5.5).
 *
 * Reads from the loopsRunnability contract via `useApi`. Renders three
 * StatCards (Total / Runnable / Roadmap-blocked) and a Progress bar that
 * shows how close the catalog is to 100% runnable. A "View roadmap →"
 * Button links to /agents/roadmap (shipped by task #339).
 *
 * No new atoms — composes Card, StatCard, Progress, Button from
 * @excellent/ui. Same surface ladder + typography ramp as the rest of
 * the optimization blocks.
 */


import * as React from "react";
import { RouterLink as Link } from "../../router";
import { Card } from "../../card";
import { Progress } from "../../progress";
import { Button } from "../../button";
import { Skeleton } from "../../skeleton";
import { useApi } from "../../api/client";
import { formatNumber } from "../../format";
import { loopsRunnability, type LoopsRunnability } from "../../api/contracts/workflows/runnability";

export function RunnabilityKpi() {
  const { data, error, isLoading } = useApi(loopsRunnability.get, {});
  if (error) return null;
  if (isLoading || !data) return <RunnabilityKpiSkeleton />;
  return <RunnabilityKpiView {...data} />;
}

function RunnabilityKpiSkeleton() {
  // Shape-matched to RunnabilityKpiView (3 stats + meta + progress bar) so the
  // loading state reads as "content arriving here", not a blank/broken card.
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton className="h-7 w-12" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
        <Skeleton className="ml-auto h-4 w-20" />
      </div>
      <Skeleton className="mt-3 h-2 w-full" />
    </Card>
  );
}

export function RunnabilityKpiView({
  total,
  runnable,
  blocked,
  blocked_roles,
}: LoopsRunnability) {
  const pct = total === 0 ? 0 : Math.round((runnable / total) * 1000) / 10;
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
        <Stat label="Workflows in catalog" value={total} />
        <Stat label="Runnable" value={runnable} accent="text-foreground" />
        <Stat label="Roadmap" value={blocked} accent={blocked === 0 ? "text-muted-foreground" : "text-foreground"} />
        <div className="ml-auto flex items-center gap-3">
          <span className="type-subtext text-muted-foreground">
            {pct.toFixed(1)}% runnable
          </span>
          {blocked > 0 ? (
            <Button variant="outline" size="sm" asChild>
              <Link href="/agents/roadmap">View roadmap →</Link>
            </Button>
          ) : (
            <Button variant="ghost" size="sm" disabled>
              All workflows runnable
            </Button>
          )}
        </div>
      </div>
      <div className="mt-3">
        <Progress value={pct} className="h-2" />
      </div>
      {blocked > 0 && blocked_roles.length > 0 ? (
        <p className="mt-2 type-subtext text-muted-foreground">
          Blocked by {blocked_roles.length} missing role
          {blocked_roles.length === 1 ? "" : "s"}:{" "}
          {blocked_roles.slice(0, 4).join(", ")}
          {blocked_roles.length > 4 ? ", …" : ""}
        </p>
      ) : null}
    </Card>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: string;
}) {
  // Label-led to match StatCard / StatStrip everywhere else (muted label above
  // the oversized tabular value) — the system's one metric ordering.
  return (
    <div className="flex flex-col gap-1">
      <span className="type-body text-muted-foreground">{label}</span>
      <span className={"type-heading font-medium leading-none tabular-nums " + (accent ?? "text-foreground")}>
        {formatNumber(value)}
      </span>
    </div>
  );
}
